import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChartBar,
  faCoins,
  faExclamationTriangle,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";
import {
  CalendarDays,
  ChevronRight,
  CircleCheck,
  CreditCard,
  Hourglass,
  Percent,
  Plus,
  ReceiptText,
  Wallet,
} from "lucide-react";
import PageHeader from "../common/PageHeader";
import HubLinkCard from "../common/HubLinkCard";
import Loader from "../common/Loader";
import SupportLink from "../root/support/components/SupportLink";
import Money, { BalanceAmount, ConvertedMoney } from "./hub/Money";
import { StatCard } from "./hub/FinanceSummary";
import { CurrencyContext } from "../../context/Context";
import { isIterableArray } from "../../helpers/utils";
import { findConfigAsProperty } from "../../helpers/api/condominium";
import {
  fetchDebtsToPayAsPropertyCuentaPendingPeriod,
  fetchDebtsToPayAsPropertyPendingTotal,
  fetchMyAliquotsAsProperty,
  findMyPaysAsProperty,
  GetMyBalance,
} from "../../helpers/api/finance";
import useSWRFetchPaginationProperty from "../../helpers/hooks/useSWRFetchPaginationProperty";

// Mis finanzas (propietario): responde "¿cuánto debo y cómo pago?". Arriba
// el total por pagar con el botón de pagar; después saldo, pagos en revisión
// y alícuota; las cuentas por período y los últimos pagos; y los accesos.
// Los movimientos del condominio viven en Condominio › Finanzas.

const BASE = "/dashboard/my/finance";
const RECENT_PAYS = 5;

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const PAY_STATUS = {
  pending: { label: "En revisión", color: "info" },
  refused: { label: "Rechazado", color: "danger" },
  canceled: { label: "Cancelado", color: "secondary" },
  voided: { label: "Anulado", color: "secondary" },
};
const payStatus = (status) => PAY_STATUS[status] || { label: "Aprobado", color: "success" };

const payReference = (pay) =>
  pay.reference && pay.reference !== "undefined"
    ? `Ref. ${String(pay.reference).toUpperCase()}`
    : "Sin referencia";

// Hook pequeño para las consultas de un solo valor de esta pantalla.
const useAsync = (load, deps, enabled = true) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let isMounted = true;
    setState((prev) => ({ ...prev, loading: true }));
    load()
      .then((data) => isMounted && setState({ data, loading: false, error: null }))
      .catch((error) => isMounted && setState({ data: null, loading: false, error }));
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, deps);

  return state;
};

// "El condominio cobra el día 1 de cada mes, con 5 días de prórroga."
const billingText = (config) => {
  if (!config?.charge_at) return null;
  const delay = Number(config.delay_at || 0);
  return `El condominio cobra el día ${config.charge_at} de cada mes${
    delay > 0 ? `, con ${plural(delay, "día", "días")} de prórroga` : ""
  }.`;
};

const DueHero = ({ total, periodsCount, loading, config }) => {
  const isUpToDate = !loading && Number(total.value || 0) <= 0;
  const billing = billingText(config);

  return (
    <Card className="hl-balance mb-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: "0.75rem" }}>
        <div>
          <span className="hl-caption">Total por pagar</span>
          {loading ? (
            <div className="hl-balance__amount text-400">…</div>
          ) : (
            <BalanceAmount value={total.value} className="hl-balance__amount" />
          )}
          {!loading && (
            <div className="fs--1 font-weight-medium text-600">
              <ConvertedMoney value={total.value} converted={total.converted} />
            </div>
          )}
        </div>
        {!loading &&
          (isUpToDate ? (
            <Badge color="success" pill className="hl-balance__status">
              <CircleCheck size={13} strokeWidth={2.25} className="mr-1" aria-hidden="true" />
              Estás al día
            </Badge>
          ) : (
            <Badge color="warning" pill className="hl-balance__status">
              <Hourglass size={13} strokeWidth={2.25} className="mr-1" aria-hidden="true" />
              {periodsCount > 0
                ? plural(periodsCount, "período pendiente", "períodos pendientes")
                : "Pagos pendientes"}
            </Badge>
          ))}
      </div>

      <div className="hl-balance__actions">
        {!isUpToDate && (
          <Button tag={Link} to={`${BASE}/topay`} color="primary">
            <CreditCard size={16} strokeWidth={2} className="mr-2" aria-hidden="true" />
            Pagar ahora
          </Button>
        )}
        <Button tag={Link} to={`${BASE}/advance/add`} color="falcon-default">
          <Plus size={16} strokeWidth={2} className="mr-2" aria-hidden="true" />
          Saldo a favor
        </Button>
        <Button tag={Link} to={`${BASE}/pays`} color="falcon-default">
          <ReceiptText size={16} strokeWidth={2} className="mr-2" aria-hidden="true" />
          Mis pagos
        </Button>
      </div>

      {billing && (
        <p className="hl-balance__footnote">
          <CalendarDays size={14} strokeWidth={2} className="mr-2" aria-hidden="true" />
          {billing}
        </p>
      )}
    </Card>
  );
};

DueHero.propTypes = {
  total: PropTypes.shape({ value: PropTypes.number, converted: PropTypes.number }).isRequired,
  periodsCount: PropTypes.number,
  loading: PropTypes.bool,
  config: PropTypes.object,
};

const ListCard = ({ title, to, linkLabel, children }) => (
  <Card className="h-100">
    <CardHeader className="d-flex align-items-center justify-content-between">
      <h5 className="mb-0">{title}</h5>
      <Link to={to} className="hl-feature-card__link mt-0">
        {linkLabel}
        <ChevronRight size={14} strokeWidth={2} className="ml-1" aria-hidden="true" />
      </Link>
    </CardHeader>
    <CardBody className="pt-0 px-2 pb-2">{children}</CardBody>
  </Card>
);

ListCard.propTypes = {
  title: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  linkLabel: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const EmptyRow = ({ children }) => (
  <p className="hl-row hl-row--muted hl-row__meta mb-0 mx-2">{children}</p>
);

EmptyRow.propTypes = { children: PropTypes.node };

const PendingPeriods = ({ periods, total, loading }) => {
  if (loading && !isIterableArray(periods)) return <Loader />;
  if (!isIterableArray(periods)) {
    return (
      <div className="hl-row hl-row--muted mx-2">
        <span className="hl-icon-tile hl-icon-tile--sm hl-icon-tile--success" aria-hidden="true">
          <CircleCheck size={16} strokeWidth={2} />
        </span>
        <div className="hl-row__body">
          <h6 className="hl-row__title">No tienes cuentas por pagar</h6>
          <p className="hl-row__meta mb-0">Cuando el condominio genere un cobro, aparecerá aquí.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {periods.map((period) => (
        <Link key={period.id} to={`${BASE}/topay`} className="hl-row">
          <span className="hl-icon-tile hl-icon-tile--sm hl-icon-tile--warning" aria-hidden="true">
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
          </span>
          <div className="hl-row__body">
            <h6 className="hl-row__title">{period.name}</h6>
            <p className="hl-row__meta mb-0">
              {period.accounts_count != null
                ? plural(period.accounts_count, "cuenta", "cuentas")
                : "Cuentas pendientes"}
              {period.control_number ? ` · N° de control ${period.control_number}` : ""}
            </p>
          </div>
          <div className="hl-row__value">
            <Money value={period.total_amount} />
            <div className="hl-row__meta font-weight-normal">
              <ConvertedMoney value={period.total_amount} converted={period.total_amount_bs} />
            </div>
          </div>
          <ChevronRight size={16} className="text-400 flex-shrink-0" aria-hidden="true" />
        </Link>
      ))}
      {total > periods.length && (
        <Link to={`${BASE}/topay`} className="hl-row__meta d-block text-center pt-2">
          Ver los {total} períodos pendientes
        </Link>
      )}
    </>
  );
};

PendingPeriods.propTypes = {
  periods: PropTypes.array,
  total: PropTypes.number,
  loading: PropTypes.bool,
};

const RecentPays = ({ pays, loading, error, rateFor }) => {
  if (loading) return <Loader />;
  if (error) return <EmptyRow>No pudimos cargar tus pagos. Intenta de nuevo más tarde.</EmptyRow>;
  if (!isIterableArray(pays)) {
    return <EmptyRow>Aún no has reportado pagos. Cuando lo hagas, verás aquí su estado.</EmptyRow>;
  }

  return pays.slice(0, RECENT_PAYS).map((pay) => {
    const status = payStatus(pay.status);
    return (
      <Link
        key={pay.id}
        className="hl-row"
        to={{
          pathname: `${BASE}/pays/${pay.id}`,
          state: {
            amount: pay.amount,
            propertyDebtId: pay.created_at,
            date: pay.created_at,
            aliquot: pay.aliquot,
          },
        }}
      >
        <div className="hl-row__body">
          <h6 className="hl-row__title">
            <Money value={pay.amount} />
          </h6>
          <p className="hl-row__meta mb-0">
            {moment(pay.created_at).format("DD MMM YYYY")} · {payReference(pay)}
          </p>
        </div>
        <div className="text-right">
          <Badge color={status.color} pill>
            {status.label}
          </Badge>
          <div className="hl-row__meta mt-1">
            <ConvertedMoney value={pay.amount} converted={pay.amount * rateFor(pay)} />
          </div>
        </div>
      </Link>
    );
  });
};

RecentPays.propTypes = {
  pays: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.any,
  rateFor: PropTypes.func.isRequired,
};

const ConfigMissing = () => (
  <Alert color="warning" className="d-flex align-items-center shadow-sm">
    <FontAwesomeIcon icon={faExclamationTriangle} size="lg" className="mr-3" />
    <div>
      <h5 className="alert-heading">Falta configurar los días de cobro del condominio</h5>
      <p className="mb-0">
        Tus finanzas se calculan a partir de los días de cobro. Pídele al administrador que los
        configure.
      </p>
    </div>
  </Alert>
);

const MyFinance = () => {
  const { selectedCondominium, condominium } = useSelector((state) => state.condominium);
  const { user } = useSelector((state) => state.auth);
  const { propertyLabel } = useSelector((state) => state.property);
  const { currency: curr } = useContext(CurrencyContext);

  const config = useAsync(
    () => findConfigAsProperty(selectedCondominium).then(({ data }) => data),
    [selectedCondominium],
    !!selectedCondominium
  );
  // Sin días de cobro el backend no calcula las finanzas (automatic llega en null).
  const ready = !!config.data && config.data.automatic !== null;

  const total = useAsync(
    () =>
      fetchDebtsToPayAsPropertyPendingTotal({ condominiumId: selectedCondominium }).then(
        ({ total_amount, total_amount_bs }) => ({
          value: Number(total_amount || 0),
          converted: total_amount_bs ?? undefined,
        })
      ),
    [selectedCondominium],
    !!selectedCondominium
  );

  const balance = useAsync(
    () => GetMyBalance().then(({ data }) => Number(data?.balance || 0)),
    [user?.id],
    !!user
  );

  const pays = useAsync(
    () =>
      findMyPaysAsProperty(1, RECENT_PAYS, { condominiumId: selectedCondominium }).then(
        ({ data }) => data || []
      ),
    [selectedCondominium, ready],
    ready
  );

  // Pagos reportados que el administrador todavía no aprueba.
  const pendingPays = useAsync(
    () =>
      findMyPaysAsProperty(1, 1, { condominiumId: selectedCondominium, status: "pending" }).then(
        ({ data, meta }) => Number(meta?.total ?? (data || []).length)
      ),
    [selectedCondominium, ready],
    ready
  );

  const aliquot = useAsync(
    () => fetchMyAliquotsAsProperty().then(({ data }) => data),
    [ready],
    ready
  );

  const {
    data: periods,
    isLoading: periodsLoading,
    meta: periodsMeta,
  } = useSWRFetchPaginationProperty(fetchDebtsToPayAsPropertyCuentaPendingPeriod, {
    state: 0,
    condominiumId: selectedCondominium,
  });
  const periodsCount = Number(periodsMeta?.total || (periods || []).length);

  // Los pagos guardan la tasa con la que se registraron; si no, la del día.
  const rateFor = (pay) => {
    const stored = Number(pay?.exchange_rate?.value ?? pay?.exchange_rate ?? 0);
    return stored > 0 ? stored : Number(curr?.value || 1);
  };

  const subtitle = [propertyLabel, condominium].filter(Boolean).join(" · ");
  const balanceValue = balance.data ?? 0;

  return (
    <>
      <PageHeader
        title="Mis finanzas"
        titleTag="h2"
        description={subtitle || "Lo que debes, lo que has pagado y tu saldo a favor."}
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>

      {config.loading ? (
        <Loader />
      ) : !ready ? (
        <ConfigMissing />
      ) : (
        <>
          <DueHero
            total={total.data || { value: 0 }}
            periodsCount={periodsCount}
            loading={total.loading}
            config={config.data}
          />

          {/* En el teléfono primero lo que se paga y lo pagado; las cifras
              después. Desde md las cifras van arriba, en una fila. */}
          <div className="d-flex flex-column">
            <Row className="order-2 order-md-1">
              <Col md={4} className="mb-3">
                <StatCard
                  icon={Wallet}
                  color={balanceValue < 0 ? "danger" : "success"}
                  label="Saldo a favor"
                  caption="Lo puedes usar para pagar tus cuentas."
                  value={balance.loading ? "…" : <Money value={balanceValue} />}
                  valueClass={balanceValue < 0 ? "text-danger" : ""}
                  to={`${BASE}/advance`}
                  linkLabel="Ver saldos"
                />
              </Col>
              <Col md={4} className="mb-3">
                <StatCard
                  icon={Hourglass}
                  color="info"
                  label="Pagos en revisión"
                  caption="Reportados, esperando la aprobación del administrador."
                  value={pendingPays.loading ? "…" : pendingPays.error ? "—" : pendingPays.data}
                  to={`${BASE}/pays`}
                  linkLabel="Ver pagos"
                />
              </Col>
              <Col md={4} className="mb-3">
                <StatCard
                  icon={Percent}
                  color="primary"
                  label="Mi alícuota"
                  caption={aliquot.data?.description || "Tu parte de los gastos comunes."}
                  value={
                    aliquot.loading
                      ? "…"
                      : aliquot.data?.value != null
                      ? `${Number(aliquot.data.value).toLocaleString("es", {
                          maximumFractionDigits: 4,
                        })} %`
                      : "—"
                  }
                />
              </Col>
            </Row>

            <Row className="order-1 order-md-2">
              <Col lg={7} className="mb-3">
                <ListCard title="Por pagar" to={`${BASE}/topay`} linkLabel="Ver todo">
                  <PendingPeriods periods={periods} total={periodsCount} loading={periodsLoading} />
                </ListCard>
              </Col>
              <Col lg={5} className="mb-3">
                <ListCard title="Últimos pagos" to={`${BASE}/pays`} linkLabel="Ver todos">
                  <RecentPays
                    pays={pays.data}
                    loading={pays.loading}
                    error={pays.error}
                    rateFor={rateFor}
                  />
                </ListCard>
              </Col>
            </Row>

            <Row className="order-3">
              <Col md={4} className="mb-3">
                <HubLinkCard
                  to={`${BASE}/reports`}
                  icon={faChartBar}
                  color="info"
                  title="Reportes"
                  description="Tus estados de cuenta y reportes por período."
                />
              </Col>
              <Col md={4} className="mb-3">
                <HubLinkCard
                  to={`${BASE}/advance`}
                  icon={faCoins}
                  color="success"
                  title="Saldos a favor"
                  description="Registra un saldo a favor y consulta los que tienes."
                />
              </Col>
              <Col md={4} className="mb-3">
                <HubLinkCard
                  to="/dashboard/finance"
                  icon={faBuilding}
                  color="secondary"
                  title="Finanzas del condominio"
                  description="Movimientos, gastos y fondos de tu condominio."
                />
              </Col>
            </Row>
          </div>
        </>
      )}
    </>
  );
};

export default MyFinance;
